/* global FWP, FWP_HTTP, awc */

import { addFilter } from '@wordpress/hooks';

const originalFacetConfigs = {};
let facetHooksAdded = false;

/**
 * Helper function to get element from fUtil object or native element
 *
 * @param {*} el - Can be fUtil object or native element
 * @return {Element|null} The element or null if not found.
 */
const getElement = ( el ) => {
	if ( el && el.nodes && el.nodes.length > 0 ) {
		return el.nodes[ 0 ];
	}

	if ( el instanceof Element ) {
		return el;
	}

	// Handle jQuery object
	if ( el && el.jquery && el.length > 0 ) {
		return el[ 0 ];
	}

	return null;
};

/**
 * Helper function to check if a value is defined
 *
 * @param {*} input - The value to check
 * @return {boolean} True if the value is defined, false otherwise
 */
const isset = ( input ) => {
	return typeof input !== 'undefined';
};

/**
 * Build choices array from element options based on selected values
 *
 * @param {Element|Object} el             - Element or fUtil object
 * @param {Array}          selectedValues - Array of selected values
 * @return {Array} Array of choice objects with value and label
 */
const buildChoicesFromElement = ( el, selectedValues ) => {
	const choices = [];
	const element = getElement( el );

	if ( ! element ) {
		return choices;
	}

	selectedValues.forEach( ( val ) => {
		const option = element.querySelector( 'option[value="' + val + '"]' );

		if ( option ) {
			choices.push( {
				value: val,
				label: option.textContent,
			} );
		}
	} );

	return choices;
};

/**
 * Helper function to wait for a condition to be true
 *
 * @param {Function} checkReadyCallback - The callback to check if the condition is true
 * @param {Function} callback           - The callback to execute when the condition is true
 * @param {number}   maxAttempts        - The maximum number of attempts to check
 * @param {number}   attemptDelay       - The delay between attempts
 * @return {void}
 */
const waitFor = ( checkReadyCallback, callback, maxAttempts = 5, attemptDelay = 50 ) => {
	let attempts = 0;

	const checkReady = () => {
		if ( checkReadyCallback() ) {
			callback();
		} else if ( attempts < maxAttempts ) {
			attempts++;
			setTimeout( checkReady, attemptDelay );
		}
		// If not ready after max attempts, that's ok - page doesn't have FacetWP
	};

	checkReady();
};

/**
 * Set reset button visibility
 *
 * @param {boolean} visible - Whether the reset button should be visible
 * @return {void}
 */
const setResetButtonVisibility = ( visible ) => {
	const resetButton = document.querySelector( '.facetwp-reset' );

	if ( resetButton ) {
		resetButton.style.display = visible ? '' : 'none';
	}
};

/**
 * Initialize FacetWP MultiSelect
 *
 * @return {void}
 */
const initializeFacetWP = () => {
	document.querySelectorAll( '.facetwp-facet.facetwp-type-select' )?.forEach( ( dropdown ) => {
		if ( ! dropdown ) {
			return;
		}

		const select = dropdown.querySelector( 'select' );

		if ( ! select ) {
			return;
		}

		if ( dropdown.querySelector( '.awc-select__wrapper' ) ) {
			return;
		}

		const facetName = dropdown.dataset.name;

		// Save original facet config on first load (before any AJAX requests)
		if ( FWP.settings && FWP.settings[ facetName ] ) {
			const currentSettings = FWP.settings[ facetName ];

			// Only save if we haven't saved this facet yet, or if current settings are more complete
			if (
				! originalFacetConfigs[ facetName ] ||
				Object.keys( currentSettings ).length >
					Object.keys( originalFacetConfigs[ facetName ] ).length
			) {
				originalFacetConfigs[ facetName ] = { ...currentSettings };
			}
		}

		// CRITICAL FIX: Use saved original config instead of current settings
		// FacetWP's AJAX refresh may not send full settings, so we use saved config
		const savedConfig = originalFacetConfigs[ facetName ] || {};
		const shouldBeMultiple = savedConfig.multiple === true || savedConfig.multiple === 'yes';

		if ( shouldBeMultiple && ! select.hasAttribute( 'multiple' ) ) {
			// FacetWP lost the 'multiple' attribute during AJAX - restore it
			select.setAttribute( 'multiple', 'multiple' );
		}

		try {
			const optionFormatter = function ( label, node ) {
				const counter = node.getAttribute( 'data-counter' );

				return counter ? label + ' (' + counter + ')' : label;
			};

			const settings = FWP.settings[ facetName ];

			if ( select.multiple ) {
				new awc.MultiSelect( {
					element: dropdown,
					optionFormatter: settings?.show_counts ? optionFormatter : undefined,
				} );
			} else {
				new awc.Select( {
					element: dropdown,
					optionFormatter: settings?.show_counts ? optionFormatter : undefined,
				} );
			}
		} catch ( error ) {
			console.error( `[MultiSelect INIT] Dropdown : Failed to initialize:`, error ); // eslint-disable-line
		}
	} );

	/**
	 * Reset button initialization - Override of FacetWP's original code.
	 *
	 * @see plugins/facetwp/assets/js/src/front-facets.js:1131
	 *
	 * Problem: Original code only runs when FWP.loaded === false, but we couldn't
	 * reliably reset that flag when switching tabs.
	 *
	 * Solution: This version runs independently of FWP.loaded state.
	 *
	 * Trade-off: Original FacetWP code may throw console errors, but this
	 * workaround ensures reset buttons function correctly.
	 */
	document.querySelectorAll( '.facetwp-type-reset' )?.forEach( ( wrapper ) => {
		const button = wrapper.querySelector( '.facetwp-reset' );

		// Skip if no button or already initialized (has _facets property)
		if ( ! button || button._facets ) {
			return;
		}

		const { name } = wrapper.dataset;
		const { mode, values } = button.dataset;

		// Determine which facets this reset button controls
		let facets = values === null ? Object.keys( FWP.facets ) : values.split( ',' );

		// Exclude mode: control all facets EXCEPT the specified ones
		if ( 'exclude' === mode ) {
			facets = Object.keys( FWP.facets ).filter(
				( facet ) => ! facets.includes( facet ) && facet !== name
			);
		}

		// Store target facets on the DOM element for later reference
		button._facets = facets;

		// Auto-hide reset button if all controlled facets are empty
		if ( ! button.classList.contains( 'facetwp-hide-empty' ) ) {
			return;
		}

		const allEmpty = facets.every(
			( val ) => typeof FWP.facets[ val ] === 'undefined' || FWP.facets[ val ].length < 1
		);

		wrapper.classList.toggle( 'facetwp-hidden', allEmpty );
	} );

	setResetButtonVisibility( true );
};

/**
 * Destroy FacetWP dropdowns
 *
 * @return {void}
 */
const destroyFacetWPDropdowns = () =>
	[ ...document.querySelectorAll( '.facetwp-facet.facetwp-type-select' ) ]
		?.filter( ( dropdown ) => dropdown._multiSelectInstance )
		.forEach( ( dropdown ) => {
			try {
				dropdown._multiSelectInstance.destroy();
			} catch ( error ) {
				console.error( `[MultiSelect DESTROY] Dropdown: Failed to destroy:`, error ); // eslint-disable-line
			}
		} );

/**
 * On tab change
 *
 * @return {void}
 */
const onTabChange = () => {
	// Destroy old instances
	destroyFacetWPDropdowns();

	setResetButtonVisibility( false );

	FWP.reset();

	waitFor(
		// Wait for both facets AND template to exist in DOM before initializing FacetWP
		// FacetWP's init() requires .facetwp-template to exist, otherwise it returns early
		() => {
			return (
				document.querySelectorAll( '.facetwp-facet[data-type]' ).length > 0 &&
				document.querySelectorAll( '.facetwp-template' ).length > 0
			);
		},
		() => {
			// Force FacetWP to fully reinitialize by resetting loaded state
			// This ensures facets are parsed and rendered fresh on every tab change
			FWP.loaded = false;

			// Clear preload data to force fresh fetch from server
			// Otherwise FWP.refresh() will use stale preload_data instead of fetching
			if ( typeof window.FWP_JSON !== 'undefined' && window.FWP_JSON.preload_data ) {
				delete window.FWP_JSON.preload_data;
			}

			// Always call init() which will:
			// 1. Call setDefaults() - resets state
			// 2. Call parseFacets() - reads facet DOM values
			// 3. Call refresh() - fetches data and renders (now forced to fetchData())
			// 4. Trigger 'facetwp-loaded' event - initializes MultiSelect
			FWP.init();
		}
	);
};

// Event: awc-select-closed - Reset active facet and trigger FacetWP autoload
const onSelectClose = ( e ) => {
	FWP.active_facet = null;

	if ( ! e.detail || ! e.detail.select || ! e.detail.changed ) {
		return;
	}

	const facet = e.detail.select.closest( '.facetwp-type-select' );

	if ( facet && ! FWP.is_refresh ) {
		FWP.autoload();
	}
};

/**
 * Add FacetWP hooks
 *
 * @return {void}
 */
const addFWPHooks = () => {
	if ( facetHooksAdded ) {
		return;
	}

	// Hook: facetwp/refresh/select
	FWP.hooks.addAction( 'facetwp/refresh/select', function ( $this, facetName ) {
		const element = getElement( $this );

		if ( ! element ) {
			return;
		}

		const selectEl = element.querySelector( 'select' );
		let val;

		if ( selectEl ) {
			// For multiple select, get all selected values
			if ( selectEl.multiple ) {
				val = Array.from( selectEl.selectedOptions ).map( ( option ) => option.value );
			} else {
				val = selectEl.value;
			}
		} else {
			val = null;
		}

		if ( null === val || '' === val ) {
			val = [];
		}

		FWP.facets[ facetName ] = Array.isArray( val ) ? val : [ val ];
	} );

	// Trick FWP to fetch the reset button HTML as well.
	FWP.hooks.addAction(
		'facetwp/refresh/reset',
		( $this, facetName ) => ( FWP.facets[ facetName ] = FWP.facets[ facetName ] ?? [] )
	);

	// Filter: facetwp/selections/select
	FWP.hooks.addFilter( 'facetwp/selections/select', function ( _output, params ) {
		return buildChoicesFromElement( params.el, params.selected_values );
	} );

	facetHooksAdded = true;
};

/**
 * Render FacetWP facet selections (filter tags)
 *
 * Custom implementation based on FacetWP's original renderSelections function,
 * with modifications to support proper cleanup when switching between tabs.
 *
 * Why custom implementation?
 * - FacetWP's default behavior doesn't provide a way to target the selections <ul> element
 *   for removal when switching tabs (it has no class by default)
 * - We need to add a custom class 'facetwp-selections-list' to the <ul> element
 *   so it can be targeted by our 'awc.pageTabs.dynamicElementSelectors' filter
 *
 * How it works:
 * - Hooks into FacetWP's 'facetwp/loaded' event
 * - Iterates through selected facets and builds HTML for selection tags
 * - Prevents FacetWP's default rendering by returning empty array in 'facetwp/selections/{type}' filter
 * - Renders selections with custom class for proper cleanup on tab changes
 *
 * @return {void}
 */
const renderSelections = () => {
	FWP.hooks.addAction(
		'facetwp/loaded',
		() => {
			let selections = '';
			const skipped = [ 'pager', 'reset', 'sort', 'search' ];
			const selectedFacets =
				! FWP.loaded && ! FWP.is_bfcache && isset( FWP_HTTP.url_vars )
					? Object.assign( FWP.facets, FWP_HTTP.url_vars )
					: FWP.facets;

			Object.keys( selectedFacets ).forEach( ( key ) => {
				const val = selectedFacets[ key ];

				if (
					val.length < 1 ||
					! isset( FWP.settings.labels[ key ] ) ||
					skipped.includes( FWP.facet_type[ key ] )
				) {
					return; // skip facet
				}

				let choices = val;
				const el = document.querySelector( `.facetwp-facet-${ key }` );
				const facetType = el?.dataset.ui || el?.dataset.type;

				choices = buildChoicesFromElement( el, choices );

				FWP.hooks.addFilter( `facetwp/selections/${ facetType }`, () => [] );

				if ( choices.length ) {
					if ( 'string' === typeof choices ) {
						choices = [ { value: '', label: choices } ];
					} else if ( ! isset( choices[ 0 ].label ) ) {
						choices = [ { value: '', label: choices[ 0 ] } ];
					}
				}

				const values = choices
					.map(
						( choice ) =>
							`<span class="facetwp-selection-value" data-value="${
								choice.value
							}">${ FWP.helper.escapeHtml( choice.label ) }</span>`
					)
					.join( '' );

				if ( '' !== values ) {
					selections += `<li data-facet="${ key }"><span class="facetwp-selection-label">${ FWP.settings.labels[ key ] }:</span> ${ values }</li>`;
				}
			} );

			if ( '' !== selections ) {
				selections = `<ul class="facetwp-selections-list">${ selections }</ul>`;
			}

			const selectionsContainer = document.querySelector( '.facetwp-selections' );
			if ( selectionsContainer ) {
				selectionsContainer.innerHTML = selections;
			}
		},
		1000
	);
};

document.addEventListener( 'facetwp-loaded', initializeFacetWP );
document.addEventListener( 'awc-after-tab-update', onTabChange );
document.addEventListener( 'awc-select-closed', onSelectClose );

waitFor( () => isset( window.FWP ), addFWPHooks );
waitFor( () => isset( window.FWP ), renderSelections );

/*
 * Register dynamic element selectors for FacetWP select elements
 * that should be cleaned up during tab updates.
 *
 * @param {Array} selectors - The existing dynamic element selectors.
 * @return {Array} The updated dynamic element selectors.
 */
addFilter( 'awc.pageTabs.dynamicElementSelectors', 'awc.facetwp.selectors', ( selectors ) => [
	...selectors,
	'.facetwp-dropdown',
	'.awc-select__dropdown',
	'.awc-select__wrapper',
	'.facetwp-input-wrap',
	'.facetwp-selections-list',
] );
